<template>
  <div
    v-if="!hideStatus"
    ref="root"
    class="Status"
    :class="[{ '-focused': focused }, { '-conversation': inlineExpanded }]"
  >
    <div
      v-if="error"
      class="alert error"
    >
      {{ error }}
      <button
        :title="$t('general.dismiss')"
        :aria-label="$t('general.dismiss')"
        class="fa-scale-110 fa-old-padding"
        type="button"
        @click="clearError"
      >
        <FAIcon icon="times" />
      </button>
    </div>
    <template v-if="muted && !isPreview">
      <div class="status-container muted">
        <small class="status-username">
          <FAIcon
            v-if="isRepeat"
            class="fa-scale-110 fa-old-padding repeat-icon"
            icon="retweet"
          />
          <UserLink
            :user="repeater"
            :at="false"
          />
        </small>
        <small class="mute-reason">
          {{ muteLocalized }}
        </small>
        <button
          :title="$t('status.toggle_muted')"
          :aria-label="$t('status.toggle_muted')"
          class="unmute button-unstyled"
          @click.prevent="toggleMute"
        >
          <FAIcon
            icon="eye-slash"
            class="fa-scale-110 fa-old-padding"
          />
        </button>
      </div>
    </template>
    <template v-else>
      <div
        v-if="!isDeleted"
        :class="[userClass, { highlighted: userStyle, '-repeat': isRepeat && !inConversation }]"
        :style="[ userStyle ]"
        class="status-container"
        :data-tags="tags"
      >
        <div
          v-if="!noHeading"
          class="left-side post-avatar-box"
        >
          <RepeatBadge
            v-if="isRepeat && !inConversation"
            :user="repeater"
          />
          <UserAvatarLink
            v-if="user.name"
            :user-id="user.id"
            :compact="compact"
            avatar-class="post-avatar"
          />
          <UserAvatar
            v-else
            :user-id="user.id"
            class="post-avatar"
            :compact="compact"
            :title="$t('status.unknown_user_info')"
          />
        </div>
        <div class="right-side">
          <div
            v-if="!noHeading"
            class="status-heading"
          >
            <div class="heading-name-row">
              <!-- floated first: only the first line makes room for it, so a
                   handle that wraps gets the full width -->
              <span class="heading-right">
                <span
                  v-if="mainStatus.pinned"
                  class="pin"
                >
                  <FAIcon
                    icon="thumbtack"
                    class="faint"
                  />
                  <span class="faint">{{ $t('status.pinned') }}</span>
                </span>
                <router-link
                  class="timeago faint"
                  :to="{ name: 'conversation', params: { id: status.id } }"
                >
                  <Timeago
                    :time="mainStatus.created_at"
                    :auto-update="60"
                  />
                </router-link>
                <span
                  v-if="mainStatus.visibility"
                  class="visibility-icon"
                  :title="visibilityLocalized"
                >
                  <FAIcon
                    fixed-width
                    class="fa-scale-110"
                    :icon="visibilityIcon(status.visibility)"
                  />
                </span>
                <span
                  v-if="mainStatus.expires_at"
                  class="expiry-icon faint"
                  role="img"
                  :title="expiryText"
                  :aria-label="expiryText"
                >
                  <FAIcon
                    fixed-width
                    icon="hourglass-half"
                  />
                </span>
                <button
                  v-if="expandable && !isPreview"
                  class="button-unstyled"
                  :title="$t('status.expand')"
                  @click.prevent="toggleExpanded"
                >
                  <FAIcon
                    fixed-width
                    class="fa-scale-110"
                    icon="plus-square"
                  />
                </button>
                <button
                  :title="$t('status.toggle_muted')"
                  :aria-label="$t('status.toggle_muted')"
                  v-if="unmuted"
                  class="button-unstyled"
                  @click.prevent="toggleMute"
                >
                  <FAIcon
                    fixed-width
                    icon="eye-slash"
                    class="fa-scale-110"
                  />
                </button>
              </span>
              <div
                v-if="user"
                class="heading-left"
              >
                <h4
                  class="status-username"
                  :title="user.name"
                >
                  <NameWithEmoji
                    :name="nameOf(user)"
                    :emoji="user.emoji"
                  />
                </h4>
                <UserLink
                  class="account-name"
                  :title="user.screen_name_full_ui"
                  :user="user"
                  :at="false"
                />
                <img
                  v-if="!!(user && user.favicon)"
                  class="status-favicon"
                  :src="user.favicon"
                >
              </div>

            </div>
            <div
              v-if="scrobblePresent"
              class="status-rich-presence"
            >
              <a
                v-if="scrobble.externalLink"
                :href="scrobble.externalLink"
                target="_blank"
              >
                {{ scrobble.artist }} — {{ scrobble.title }}
                <FAIcon
                  class="fa-scale-110 fa-old-padding"
                  icon="play"
                />
                <span class="status-rich-presence-time">
                  <Timeago
                    template-key="time.in_past"
                    :time="scrobble.created_at"
                    :auto-update="60"
                  />
                </span>
              </a>
              <span v-if="!scrobble.externalLink">
                <FAIcon
                  class="fa-scale-110 fa-old-padding"
                  icon="music"
                />
                {{ scrobble.artist }} — {{ scrobble.title }}
                <FAIcon
                  class="fa-scale-110 fa-old-padding"
                  icon="play"
                />
                <span class="status-rich-presence-time">
                  <Timeago
                    template-key="time.in_past"
                    :time="scrobble.created_at"
                    :auto-update="60"
                  />
                </span>
              </span>
            </div>
            <div
              v-if="showReplyLine || hasMentionsLine"
              class="heading-reply-row"
            >
              <span
                v-if="showReplyLine"
                class="glued-label reply-glued-label"
              >
                <i18n-t
                  keypath="status.reply_to_with_arg"
                  scope="global"
                >
                  <template #replyToWithIcon>
                    <StatusPopover
                      v-if="!isPreview"
                      :status-id="mainStatus.parent_visible && mainStatus.in_reply_to_status_id"
                      class="reply-to-popover"
                      style="min-width: 0;"
                      :class="{ '-strikethrough': !mainStatus.parent_visible }"
                    >
                      <button
                        class="button-unstyled reply-to"
                        :aria-label="$t('tool_tip.reply')"
                        @click.prevent="gotoOriginal(status.in_reply_to_status_id)"
                      >
                        <i18n-t
                          keypath="status.reply_to_with_icon"
                          scope="global"
                        >
                          <template #icon>
                            <FAIcon
                              class="fa-scale-110 fa-old-padding"
                              icon="reply"
                              flip="horizontal"
                            />
                          </template>
                          <template #replyTo>
                            <span
                              class="reply-to-text"
                            >
                              {{ $t('status.reply_to') }}
                            </span>
                          </template>
                        </i18n-t>
                      </button>
                    </StatusPopover>

                    <span
                      v-else
                      class="reply-to-no-popover"
                    >
                      <span class="reply-to-text">{{ $t('status.reply_to') }}</span>
                    </span>
                  </template>
                  <template #user>
                    <MentionLink
                      :content="replyToName"
                      :url="replyProfileLink"
                      :user-id="status.in_reply_to_user_id"
                      :user-screen-name="status.in_reply_to_screen_name"
                    />
                  </template>
                </i18n-t>
              </span>

              <!-- This little wrapper is made for sole purpose of "gluing" -->
              <!-- "Mentions" label to the first mention -->
              <span
                v-if="hasMentionsLine"
                class="glued-label"
              >
                <span
                  role="link"
                  tabindex="0"
                  @keydown.enter.prevent="gotoOriginal(status.in_reply_to_status_id)"
                  class="mentions"
                  :aria-label="$t('tool_tip.mentions')"
                  @click.prevent="gotoOriginal(status.in_reply_to_status_id)"
                >
                  <span
                    class="mentions-text"
                  >
                    {{ $t('status.mentions') }}
                  </span>
                </span>
                <MentionsLine
                  v-if="hasMentionsLine"
                  :mentions="mentionsLine.slice(0, 1)"
                  class="mentions-line-first"
                />
              </span>
              {{ ' ' }}
              <MentionsLine
                v-if="hasMentionsLine"
                :mentions="mentionsLine.slice(1)"
                class="mentions-line"
              />
            </div>
            <div
              v-if="isEdited && editingAvailable && !isPreview"
              class="heading-edited-row"
            >
              <i18n-t
                scope="global"
                keypath="status.edited_at"
                tag="span"
              >
                <template #time>
                  <Timeago
                    template-key="time.in_past"
                    :time="mainStatus.edited_at"
                    :auto-update="60"
                    :long-format="true"
                  />
                </template>
              </i18n-t>
            </div>
          </div>

          <StatusContent
            ref="content"
            :status="mainStatus"
            :focused="focused"
            :in-conversation="inConversation"
            :reply-arrow="replyArrow"
            @mediaplay="addMediaPlaying($event)"
            @mediapause="removeMediaPlaying($event)"
            @parse-ready="setHeadTailLinks"
          />
          <StatusTranslation :status="mainStatus" />

          <Quote
            :status-id="quoteId"
            :status-url="quoteUrl"
            :status-visible="quoteVisible"
          />

          <!-- one row: the actions, then the star, the reactions and the
               picker as chips; on an opened post the counts list people -->
          <StatusActionButtons
            v-if="!noHeading && !isPreview"
            class="status-action-buttons"
            :status="mainStatus"
            :replying="replyMode === 'reply'"
            :quoting="replyMode === 'quote'"
            :favorited-by="focused ? favoritedBy : undefined"
            :repeated-by="focused ? repeatedBy : undefined"
            @toggle-replying="toggleReplyForm"
            @toggle-quoting="toggleQuoteForm"
          >
            <template #reactions>
              <EmojiReactions
                v-if="mergedConfig.emojiReactionsOnTimeline || focused"
                :status="mainStatus"
              />
            </template>
          </StatusActionButtons>
        </div>
      </div>
      <div
        v-else
        class="gravestone"
      >
        <div class="left-side">
          <UserAvatar
            class="post-avatar"
            :compact="compact"
          />
        </div>
        <div class="right-side">
          <div class="deleted-text">
            {{ $t('status.status_deleted') }}
          </div>
        </div>
      </div>
      <div
        v-if="replyMode"
        class="status-container reply-form"
      >
        <PostStatusForm
          ref="postStatusForm"
          class="reply-body"
          :closeable="true"
          :replied-status="mainStatus"
          :quoting="replyMode === 'quote'"
          @posted="closeReplyForm"
          @draft-done="closeReplyForm"
          @close-accepted="closeReplyForm"
        />
      </div>
    </template>
  </div>
</template>

<script src="./status.js"></script>

<style src="./status.scss" lang="scss"></style>
